import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Typography,
  TextField,
  Paper,
  Alert,
  AppBar,
  Toolbar,
  Grid,
  CircularProgress,
  IconButton
} from '@mui/material';
import {
  DirectionsCar as CarIcon,
  Email as EmailIcon,
  Send as SendIcon,
  CheckCircleOutline as CheckIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import axios from 'axios';

const ContactPage: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    surname: '',
    email: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    document.title = "Contact Us - adiLessons";
    
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'Get in touch with adiLessons support. Submit your questions, feedback, or inquiries directly to our team.');
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const { name, surname, email, message } = formData;

    if (!name || !surname || !email || !message) {
      setError('Please fill in all required fields.');
      return;
    }

    if (message.length < 10) {
      setError('Message must be at least 10 characters long.');
      return;
    }

    try {
      setError('');
      setLoading(true);
      
      const baseUrl = process.env.REACT_APP_API_URL || '';
      await axios.post(`${baseUrl}/contact`, formData);
      
      setSuccess(true);
      setFormData({ name: '', surname: '', email: '', message: '' });
    } catch (err: any) {
      console.error('Contact submission failed:', err);
      let errMsg = 'Failed to send your message. Please try again later.';
      if (err.response?.data?.message) {
        errMsg = err.response.data.message;
      } else if (err.response?.data?.errors) {
        const firstError = Object.values(err.response.data.errors)[0];
        if (Array.isArray(firstError) && firstError.length > 0) {
          errMsg = firstError[0] as string;
        }
      }
      setError(errMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#f4f6f9' }}>
      {/* Navbar */}
      <AppBar position="sticky" sx={{ background: 'rgba(255, 255, 255, 0.9)', backdropFilter: 'blur(10px)', color: '#1a237e', boxShadow: '0 2px 12px rgba(0, 0, 0, 0.05)' }}>
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ justifyContent: 'space-between', height: 72 }}>
            <Box display="flex" alignItems="center" gap={1} onClick={() => navigate('/')} sx={{ cursor: 'pointer' }}>
              <CarIcon sx={{ color: '#1967D2', fontSize: 32 }} />
              <Typography 
                variant="h5" 
                sx={{ 
                  fontWeight: 'bold', 
                  color: '#2b3154', 
                  letterSpacing: '-0.5px',
                  fontFamily: '"Outfit", "Inter", sans-serif'
                }}
              >
                adi<span style={{ color: '#E53935', fontWeight: '900' }}>L</span>essons
              </Typography>
            </Box>

            <Button
              variant="outlined"
              size="small"
              onClick={() => navigate('/')}
              startIcon={<ArrowBackIcon />}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 'bold' }}
            >
              Back to Home
            </Button>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Main Body */}
      <Container maxWidth="md" sx={{ py: { xs: 6, md: 10 }, flexGrow: 1, display: 'flex', alignItems: 'center' }}>
        <Grid container spacing={4} alignItems="stretch">
          {/* Info Card */}
          <Grid size={{ xs: 12, md: 5 }} sx={{ display: 'flex' }}>
            <Box 
              sx={{ 
                bgcolor: '#1a237e', 
                color: 'white', 
                p: 4, 
                borderRadius: 4, 
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                boxShadow: '0 8px 24px rgba(26, 35, 126, 0.15)',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              <Box sx={{ position: 'absolute', top: -50, right: -50, width: 150, height: 150, borderRadius: '50%', background: 'radial-gradient(circle, rgba(25, 118, 210, 0.3) 0%, rgba(255,255,255,0) 70%)', pointerEvents: 'none' }} />
              
              <Box>
                <Typography variant="h4" fontWeight="800" sx={{ mb: 2 }}>
                  Contact Us
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.85, mb: 4, lineHeight: 1.6 }}>
                  Have questions about our scheduling plans, WhatsApp reminder device pairing, or pricing details? Send us a message and our support team will get back to you shortly.
                </Typography>

                <Box display="flex" flexDirection="column" gap={3}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <EmailIcon sx={{ color: '#64b5f6' }} />
                    <Box>
                      <Typography variant="caption" sx={{ opacity: 0.7, display: 'block' }}>Email Support</Typography>
                      <Typography variant="body2" fontWeight="bold" component="a" href="mailto:support@adilesson.co.uk" sx={{ color: 'white', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                        support@adilesson.co.uk
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>

              <Box sx={{ mt: 4 }}>
                <Typography variant="caption" sx={{ opacity: 0.7, display: 'block' }}>Estimated Response Time</Typography>
                <Typography variant="body2" fontWeight="bold">Within 24 business hours</Typography>
              </Box>
            </Box>
          </Grid>

          {/* Form Card */}
          <Grid size={{ xs: 12, md: 7 }}>
            <Paper elevation={0} sx={{ p: 4, borderRadius: 4, border: '1px solid #eceff1', height: '100%' }}>
              {success ? (
                <Box sx={{ textAlign: 'center', py: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  <CheckIcon sx={{ fontSize: 64, color: '#2e7d32', mb: 2 }} />
                  <Typography variant="h5" fontWeight="bold" color="text.primary" gutterBottom>
                    Message Sent Successfully!
                  </Typography>
                  <Typography variant="body2" color="text.secondary" align="center" sx={{ maxWidth: 300, mb: 4 }}>
                    Thank you for writing to us. A confirmation email has been sent to you, and we will get back to you shortly.
                  </Typography>
                  <Button 
                    variant="contained" 
                    onClick={() => setSuccess(false)}
                    sx={{ borderRadius: 2.5, px: 4, py: 1, textTransform: 'none', fontWeight: 'bold' }}
                  >
                    Send Another Message
                  </Button>
                </Box>
              ) : (
                <Box component="form" onSubmit={handleSubmit} noValidate sx={{ display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h5" fontWeight="bold" color="#1a237e" gutterBottom>
                      Write to Us
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Fill out the form below and we will contact you.
                    </Typography>

                    {error && (
                      <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                        {error}
                      </Alert>
                    )}

                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                          required
                          fullWidth
                          id="name"
                          label="Name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          disabled={loading}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                          required
                          fullWidth
                          id="surname"
                          label="Surname"
                          name="surname"
                          value={formData.surname}
                          onChange={handleChange}
                          disabled={loading}
                        />
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <TextField
                          required
                          fullWidth
                          id="email"
                          label="Email Address"
                          name="email"
                          autoComplete="email"
                          value={formData.email}
                          onChange={handleChange}
                          disabled={loading}
                        />
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <TextField
                          required
                          fullWidth
                          id="message"
                          label="Your Message"
                          name="message"
                          multiline
                          rows={4}
                          value={formData.message}
                          onChange={handleChange}
                          disabled={loading}
                          placeholder="How can we help you?"
                        />
                      </Grid>
                    </Grid>
                  </Box>

                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    endIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                    disabled={loading}
                    sx={{ mt: 4, py: 1.5, borderRadius: 2.5, fontWeight: 'bold', textTransform: 'none' }}
                  >
                    {loading ? 'Sending...' : 'Send Message'}
                  </Button>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Footer */}
      <Box sx={{ py: 3, bgcolor: '#0d132a', color: '#90a4ae', textAlign: 'center', borderTop: '1px solid #1c2a4a', mt: 'auto' }}>
        <Typography variant="caption">
          © {new Date().getFullYear()} adiLessons. All rights reserved.
        </Typography>
      </Box>
    </Box>
  );
};

export default ContactPage;
